import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Truck, Undo2, User, Package, CircleDot, CheckCircle2, ExternalLink, RefreshCw, Ban, CreditCard, ScanLine, Phone, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { Tooltip } from '@/components/ui/Tooltip';
import { useToast } from '@/components/ui/ToastContext';
import { useSipPhoneContext } from '@/contexts/SipPhoneContext';
import { listOrderCalls, type OrderCallRow } from '@/lib/sipApi';
import { supabase } from '@/lib/supabaseClient';
import { formatPaymentMethodLabel } from '@/lib/paymentStatus';
import { PathaoLocationModal } from '@/components/orders/PathaoLocationModal';
import type { PathaoLocation } from '@/components/orders/PathaoLocationModal';
import { ShipModal } from '@/components/orders/ShipModal';
import type { ShipSubmission } from '@/components/orders/ShipModal';
import { InvoiceCard } from './components/InvoiceCard';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';
import { compareProducts } from '@/lib/productOrder';

const STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  placed: 'primary',
  confirmed: 'primary',
  shipped: 'warning',
  dispatched: 'warning',
  delivered: 'success',
  cod_collected: 'success',
  remitted: 'success',
  settled: 'success',
  cancelled: 'error',
  returned: 'error',
};
// Same convention as the Orders list — a walk-in sale or a gift handed
// over/mailed without a courier still ends at status 'delivered'
// internally (Followup scheduling/reports stay unchanged), but the badge
// says "picked up" so staff don't read it as courier-confirmed.
function orderStatusLabel(o: { status: string; requires_delivery: boolean }): string {
  if (o.status === 'delivered' && !o.requires_delivery) return 'picked up';
  return o.status;
}

interface FraudResult {
  courier: string;
  display_name: string;
  ok: boolean;
  total?: number;
  delivered?: number;
  success_ratio?: number;
}

// Auto-populated by core-place-order right after checkout (see
// core_customer_fraud_cache) — shows the most conservative (lowest) ratio
// among couriers that actually have delivery history for this number, so
// staff see a risk signal without opening the separate Fraud Check tool.
// A cache miss (still processing, or nobody has history yet) shows nothing.
function worstFraudRating(results: FraudResult[] | null): { ratio: number; label: string; tone: 'success' | 'warning' | 'error' } | null {
  const withHistory = (results ?? []).filter((r) => r.ok && (r.total ?? 0) > 0);
  if (withHistory.length === 0) return null;
  const worst = withHistory.reduce((a, b) => ((a.success_ratio ?? 0) <= (b.success_ratio ?? 0) ? a : b));
  const ratio = worst.success_ratio ?? 0;
  if (ratio >= 90) return { ratio, label: 'Excellent', tone: 'success' };
  if (ratio >= 70) return { ratio, label: 'Good', tone: 'warning' };
  return { ratio, label: 'Risky', tone: 'error' };
}

const CALL_STATUS_TONE: Record<string, 'primary' | 'success' | 'warning' | 'error' | 'neutral'> = {
  ringing: 'warning',
  completed: 'success',
  no_answer: 'neutral',
  busy: 'neutral',
  failed: 'error',
  missed: 'error',
  rejected: 'error',
  canceled: 'neutral',
  transferred: 'primary',
};
const CALL_STATUS_LABEL: Record<string, string> = {
  no_answer: 'No Answer',
};

interface OrderDetail {
  id: string;
  order_no: string;
  channel: string;
  status: string;
  payment_method: string | null;
  subtotal_bdt: number | null;
  total_bdt: number;
  customer_info: { name?: string; phone?: string; address?: string } | null;
  customer_id: string | null;
  placed_at: string;
  updated_at: string;
  cancellation_reason: string | null;
  requires_delivery: boolean;
  seller_ref: string | null;
}

const PICKK_CHANNELS = ['pickk_seller', 'pickk_gb'];

interface OrderItem {
  id: string;
  sku: string;
  qty: number;
  unit_price_bdt: number | null;
  line_total_bdt: number | null;
}

interface Shipment {
  id: string;
  courier: string | null;
  courier_partner_id: string | null;
  tracking_no: string | null;
  dispatched_at: string | null;
  delivered_at: string | null;
  cod_status: string;
  courier_status: string | null;
  status_checked_at: string | null;
}

interface ReturnRow {
  id: string;
  type: string;
  reason: string | null;
  status: string;
}

interface CourierOption {
  courier_key: string;
  display_name: string;
  courier_type: string;
  is_default: boolean;
}

export default function RxkOrderDetail() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const phone = useSipPhoneContext();
  const [orderCalls, setOrderCalls] = useState<OrderCallRow[]>([]);

  const callNumber = (number: string) => {
    if (!orderId) return;
    if (phone.registrationStatus !== 'registered') {
      push('error', phone.hasCredentials ? 'Phone is not connected yet — check the status in Communications.' : 'No SIP extension set up for you yet — ask an admin to set one up in ERP.');
      return;
    }
    phone.call(number, orderId);
  };

  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [returns, setReturns] = useState<ReturnRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shipModal, setShipModal] = useState(false);
  const [returnModal, setReturnModal] = useState(false);
  const [returnForm, setReturnForm] = useState({ type: 'refund', reason: '', qty: '' });
  const [pathaoModalOpen, setPathaoModalOpen] = useState(false);
  const [pathaoBusy, setPathaoBusy] = useState(false);
  const [checkingStatusId, setCheckingStatusId] = useState<string | null>(null);
  const [cancelModal, setCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [couriers, setCouriers] = useState<CourierOption[]>([]);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [selectedCourierKey, setSelectedCourierKey] = useState('');
  const [codModal, setCodModal] = useState(false);
  const [codForm, setCodForm] = useState({ amount_bdt: '', note: '' });
  const [scannedCounts, setScannedCounts] = useState<Record<string, number>>({});
  const [bundleComponents, setBundleComponents] = useState<Record<string, { component_sku: string; qty_per_bundle: number }[]>>({});
  const [scanUnitInput, setScanUnitInput] = useState('');
  const [scanningUnit, setScanningUnit] = useState(false);
  const [fraudResults, setFraudResults] = useState<FraudResult[] | null>(null);
  const [gatewayNames, setGatewayNames] = useState<Record<string, string>>({});
  const [liveCourierName, setLiveCourierName] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    const [orderRes, itemRes, shipRes, returnRes, courierRes, gatewayRes] = await Promise.all([
      supabase.from('core_orders').select('id, order_no, channel, status, payment_method, subtotal_bdt, total_bdt, customer_info, customer_id, placed_at, updated_at, cancellation_reason, requires_delivery, seller_ref').eq('id', orderId).maybeSingle(),
      supabase.from('core_order_items').select('id, sku, qty, unit_price_bdt, line_total_bdt').eq('order_id', orderId),
      supabase.from('core_shipments').select('id, courier, courier_partner_id, tracking_no, dispatched_at, delivered_at, cod_status, courier_status, status_checked_at').eq('order_id', orderId).order('created_at', { ascending: false }),
      supabase.from('core_returns').select('id, type, reason, status').eq('order_id', orderId).order('requested_at', { ascending: false }),
      supabase.from('core_courier_partners').select('courier_key, display_name, courier_type, is_default').eq('is_enabled', true).order('display_order'),
      supabase.from('core_payment_gateways').select('gateway_key, display_name'),
    ]);
    setGatewayNames(Object.fromEntries(((gatewayRes.data as { gateway_key: string; display_name: string }[]) ?? []).map((g) => [g.gateway_key, g.display_name])));
    const orderRow = (orderRes.data as OrderDetail) ?? null;
    setOrder(orderRow);
    const phoneNorm = orderRow?.customer_info?.phone?.replace(/\D/g, '').slice(-10);
    if (phoneNorm && phoneNorm.length >= 7) {
      supabase
        .from('core_customer_fraud_cache')
        .select('results')
        .eq('phone_norm', phoneNorm)
        .maybeSingle()
        .then(({ data }) => setFraudResults((data?.results as FraudResult[]) ?? null));
    } else {
      setFraudResults(null);
    }
    const itemRows = (itemRes.data as OrderItem[]) ?? [];
    setItems(itemRows);
    const shipmentRows = (shipRes.data as Shipment[]) ?? [];
    setShipments(shipmentRows);
    // Live courier name via the FK, not the frozen `courier` text column —
    // deliberately NOT filtered by is_enabled (unlike courierRes above,
    // used only for the ship-modal picker) since a since-disabled courier
    // that actually shipped this order should still resolve its real name.
    const courierPartnerId = shipmentRows[0]?.courier_partner_id;
    if (courierPartnerId) {
      supabase
        .from('core_courier_partners')
        .select('display_name')
        .eq('id', courierPartnerId)
        .maybeSingle()
        .then(({ data }) => setLiveCourierName(data?.display_name ?? null));
    } else {
      setLiveCourierName(null);
    }
    setReturns((returnRes.data as ReturnRow[]) ?? []);
    setCouriers((courierRes.data as CourierOption[]) ?? []);
    listOrderCalls(orderId).then(setOrderCalls).catch(() => {});

    const { data: scannedRows } = await supabase.from('core_product_authenticity_codes').select('sku').eq('sold_order_id', orderId);
    const scannedMap: Record<string, number> = {};
    ((scannedRows as { sku: string }[]) ?? []).forEach((r) => { scannedMap[r.sku] = (scannedMap[r.sku] ?? 0) + 1; });
    setScannedCounts(scannedMap);

    const lineSkus = [...new Set(itemRows.map((i) => i.sku))];
    // A bundle SKU (e.g. SET-3STEP) never has its own authenticity codes —
    // it's assembled from components at sale time — so "scanned" progress
    // for a bundle line has to be read per component, not off the bundle
    // sku itself (which never appears in core_product_authenticity_codes).
    let bundleMap: Record<string, { component_sku: string; qty_per_bundle: number }[]> = {};
    if (lineSkus.length > 0) {
      const { data: bundleRows } = await supabase.from('core_product_bundles').select('bundle_sku, component_sku, qty_per_bundle').in('bundle_sku', lineSkus);
      for (const b of (bundleRows as { bundle_sku: string; component_sku: string; qty_per_bundle: number }[]) ?? []) {
        (bundleMap[b.bundle_sku] ??= []).push({ component_sku: b.component_sku, qty_per_bundle: b.qty_per_bundle });
      }
      // Set parts in routine order (Booster, Serum, Cream), not DB order.
      for (const k of Object.keys(bundleMap)) bundleMap[k].sort((a, b) => compareProducts({ sku: a.component_sku }, { sku: b.component_sku }));
      setBundleComponents(bundleMap);
    }

    const skus = [...new Set([...lineSkus, ...Object.values(bundleMap).flat().map((c) => c.component_sku)])];
    if (skus.length > 0) {
      const { data: prodRows } = await supabase.from('core_products').select('sku, name_en').in('sku', skus);
      const map: Record<string, string> = {};
      ((prodRows as { sku: string; name_en: string | null }[]) ?? []).forEach((p) => { map[p.sku] = p.name_en || p.sku; });
      setProductNames(map);
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    load();
  }, [load]);

  const advance = async (next: string) => {
    if (!order) return;
    setBusy(true);
    const { error } = await supabase.rpc('core_advance_order', { p_order_id: order.id, p_next: next });
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', `Order marked ${next}`);
    load();
  };

  const handleScanUnit = async (e: FormEvent) => {
    e.preventDefault();
    if (!order || !scanUnitInput.trim()) return;
    setScanningUnit(true);
    const { data, error } = await supabase.rpc('core_scan_link_order_unit', {
      p_inventory_code: scanUnitInput.trim().toUpperCase(),
      p_order_id: order.id,
    });
    setScanningUnit(false);
    if (error) {
      push('error', error.message);
      return;
    }
    const result = data as { sku: string; scanned_for_sku: number };
    setScannedCounts((prev) => ({ ...prev, [result.sku]: result.scanned_for_sku }));
    push('success', `Linked — ${productNames[result.sku] ?? result.sku} (${result.scanned_for_sku}/${items.find((i) => i.sku === result.sku)?.qty ?? '?'})`);
    setScanUnitInput('');
  };

  // Walk-in sale (or a gift handed over / mailed separately) — no courier is
  // ever involved, so there's nothing to book or ship. Goes straight through
  // to 'delivered' in the same click (rather than sitting at 'confirmed'
  // forever with no way to close out from here), which also schedules its
  // Customer Followup like any other delivered order. Mirrors the same
  // branch in admin/orders/page.tsx's handleConfirm.
  const handleWalkInConfirm = async () => {
    if (!order) return;
    setBusy(true);
    try {
      const { error: confirmErr } = await supabase.rpc('core_advance_order', { p_order_id: order.id, p_next: 'confirmed' });
      if (confirmErr) throw confirmErr;
      const { error: deliverErr } = await supabase.rpc('core_advance_order', { p_order_id: order.id, p_next: 'delivered' });
      if (deliverErr) throw deliverErr;
      push('success', 'Order confirmed');
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to confirm order');
    } finally {
      setBusy(false);
    }
  };

  const openConfirmModal = () => {
    const def = couriers.find((c) => c.is_default) ?? couriers[0];
    setSelectedCourierKey(def?.courier_key ?? '');
    setConfirmModalOpen(true);
  };

  const handleConfirm = async (courierKey: string) => {
    if (!order) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('core-book-shipment', { body: { order_id: order.id, courier_key: courierKey } });
      if (error) throw error;
      if (data?.already_booked) {
        push('success', `Already booked — tracking ${data.tracking_no}`);
      } else if (data?.booked) {
        push('success', `Confirmed & auto-shipped via ${data.courier} — tracking ${data.tracking_no}`);
      } else if (data?.error === 'pathao_location_required') {
        setConfirmModalOpen(false);
        setPathaoModalOpen(true);
        return; // waiting on the picker — don't reload yet
      } else if (data?.confirmed) {
        push('error', `Order confirmed, but auto-booking failed: ${data.details ?? data.error}. Ship manually or fix courier settings.`);
      } else {
        push('error', data?.details ?? data?.error ?? 'Failed to confirm order');
      }
      setConfirmModalOpen(false);
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to confirm order');
    } finally {
      setBusy(false);
    }
  };

  const handlePathaoLocationSubmit = async (location: PathaoLocation) => {
    if (!order) return;
    setPathaoBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke('core-book-shipment', { body: { order_id: order.id, courier_key: selectedCourierKey || 'pathao', pathao_location: location } });
      if (error) throw error;
      if (data?.booked) {
        push('success', `Confirmed & auto-shipped via Pathao — tracking ${data.tracking_no}`);
      } else {
        push('error', `Pathao booking failed: ${data?.details ?? data?.error}. Ship manually or retry.`);
      }
      setPathaoModalOpen(false);
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to book with Pathao');
    } finally {
      setPathaoBusy(false);
    }
  };

  const handleCheckStatus = async (shipmentId: string) => {
    setCheckingStatusId(shipmentId);
    try {
      const { data, error } = await supabase.functions.invoke('core-check-shipment-status', { body: { shipment_id: shipmentId } });
      if (error) throw error;
      if (data?.ok) {
        push('success', `Latest status: ${data.courier_status}`);
      } else {
        push('error', data?.details ?? data?.error ?? 'Failed to check status');
      }
      load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to check status');
    } finally {
      setCheckingStatusId(null);
    }
  };

  const handleShip = async (submission: ShipSubmission) => {
    if (!order) return;
    setBusy(true);
    const { error } = await supabase.from('core_shipments').insert({
      order_id: order.id,
      courier: submission.courierName,
      courier_partner_id: submission.courierPartnerId,
      tracking_no: submission.trackingNo,
      booked_via: 'manual',
      dispatched_at: new Date().toISOString(),
    });
    if (!error) await supabase.from('core_orders').update({ status: 'shipped' }).eq('id', order.id);
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Shipment created (배송 등록됨)');
    setShipModal(false);
    load();
  };

  const handleCancelOrder = async (e: FormEvent) => {
    e.preventDefault();
    if (!order || !cancelReason.trim()) return;
    setBusy(true);
    const { error: advanceErr } = await supabase.rpc('core_advance_order', { p_order_id: order.id, p_next: 'cancelled' });
    if (!advanceErr) {
      await supabase.from('core_orders').update({ cancellation_reason: cancelReason.trim() }).eq('id', order.id);
    }
    setBusy(false);
    if (advanceErr) {
      push('error', advanceErr.message);
      return;
    }
    push('success', 'Order cancelled');
    setCancelModal(false);
    setCancelReason('');
    load();
  };

  // core_approve_return only restocks when the return's qty is a positive
  // number — it used to always be NULL because this form never collected
  // one, so every "Approve + Restock" silently restocked nothing while
  // still marking the return "Restocked".
  const handleRequestReturn = async (e: FormEvent) => {
    e.preventDefault();
    if (!order) return;
    const qty = Number(returnForm.qty);
    if (!Number.isFinite(qty) || qty <= 0) {
      push('error', 'Enter how many units are being returned (Qty to restock)');
      return;
    }
    setBusy(true);
    const { error } = await supabase.from('core_returns').insert({ order_id: order.id, type: returnForm.type, reason: returnForm.reason, qty, status: 'requested' });
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Return requested (반품 요청됨)');
    setReturnModal(false);
    setReturnForm({ type: 'refund', reason: '', qty: '' });
    load();
  };

  const handleRecordCod = async (e: FormEvent) => {
    e.preventDefault();
    if (!order) return;
    setBusy(true);
    const { error } = await supabase.rpc('core_record_cod', {
      p_order_id: order.id,
      p_amount: Number(codForm.amount_bdt),
      p_note: codForm.note || null,
    });
    setBusy(false);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'COD collection recorded (COD 수금 기록됨)');
    setCodModal(false);
    setCodForm({ amount_bdt: '', note: '' });
    load();
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (!order) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Order not found" />
      </AppShell>
    );
  }

  const fraudRating = worstFraudRating(fraudResults);
  const latestShipment = shipments[0] ?? null;
  // Walk-in sales / gifts skip the courier pipeline entirely (see
  // core_advance_order's confirmed->delivered transition) — there's never
  // a shipment row for these, so pulling Shipped/Delivered timestamps from
  // latestShipment always shows "—" even once the order is actually done.
  // Use the order's own updated_at instead, and drop the Shipped step
  // (there was never a courier leg to show).
  const timeline = order.requires_delivery
    ? [
        { label: 'Placed (접수)', at: order.placed_at, done: true },
        { label: 'Shipped (배송)', at: latestShipment?.dispatched_at ?? null, done: !!latestShipment?.dispatched_at },
        { label: 'Delivered (배송완료)', at: latestShipment?.delivered_at ?? null, done: !!latestShipment?.delivered_at },
      ]
    : [
        { label: 'Placed (접수)', at: order.placed_at, done: true },
        { label: 'Picked Up (수령 완료)', at: order.status === 'delivered' ? order.updated_at : null, done: order.status === 'delivered' },
      ];
  if (order.status === 'cancelled') timeline.push({ label: 'Cancelled (취소됨)', at: order.updated_at, done: true });

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/orders')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back to orders">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-[20px] font-bold text-ink">{order.order_no}</h1>
              <p className="text-[12px] text-ink-faint">{new Date(order.placed_at).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {PICKK_CHANNELS.includes(order.channel) && (
              <Badge tone="info">via Pick-k{order.seller_ref ? ` — ${order.seller_ref}` : ''}</Badge>
            )}
            {order.status === 'cancelled' && order.cancellation_reason ? (
              <Tooltip content={order.cancellation_reason}>
                <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>{orderStatusLabel(order)}</Badge>
              </Tooltip>
            ) : (
              <Badge tone={STATUS_TONE[order.status] ?? 'neutral'}>{orderStatusLabel(order)}</Badge>
            )}
            {(order.status === 'placed' || order.status === 'confirmed') && (
              <>
                {/* Walk-in sales (and a gift handed over / mailed separately)
                    never involve a courier — nothing to confirm/retry/ship.
                    Checked via the persisted requires_delivery column, not
                    the order_no prefix (a walk-in gift gets GFT-, same as
                    admin/orders/page.tsx's list-view fix for this). Without
                    this, these orders got funneled through the courier
                    Confirm/Retry Booking flow and could get stuck at
                    'confirmed' forever with a "Retry Booking" that can never
                    succeed, since there was never a shipment to book. */}
                {order.requires_delivery ? (
                  <>
                    <Button size="sm" onClick={openConfirmModal} loading={busy}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> {order.status === 'confirmed' ? 'Retry Booking' : 'Confirm'}
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setShipModal(true)}>
                      <Truck className="w-3.5 h-3.5" /> Ship
                    </Button>
                  </>
                ) : (
                  <>
                    {order.status === 'placed' && (
                      <Button size="sm" onClick={handleWalkInConfirm} loading={busy}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
                      </Button>
                    )}
                    {/* Recovery path for an order already stuck at 'confirmed'
                        from before this fix (courier flow advanced it there
                        without ever being able to finish). */}
                    {order.status === 'confirmed' && (
                      <Button size="sm" onClick={() => advance('delivered')} loading={busy}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark Picked Up
                      </Button>
                    )}
                  </>
                )}
                <Button size="sm" variant="secondary" onClick={() => setCancelModal(true)} disabled={busy}>
                  <Ban className="w-3.5 h-3.5" /> Cancel Order
                </Button>
              </>
            )}
            {order.status === 'shipped' && (
              <Button size="sm" onClick={() => advance('delivered')} loading={busy}>
                <CheckCircle2 className="w-3.5 h-3.5" /> Mark Delivered
              </Button>
            )}
            {order.status !== 'cancelled' && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setReturnForm({ type: 'refund', reason: '', qty: String(items.reduce((sum, it) => sum + it.qty, 0)) });
                  setReturnModal(true);
                }}
              >
                <Undo2 className="w-3.5 h-3.5" /> Return
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 space-y-5">
            <Card>
              <CardHeader title="Line Items (주문 항목)" icon={<Package className="w-4 h-4" />} subtitle={`${items.length} item(s)`} />
              <CardBody className="space-y-1">
                {items.length === 0 ? (
                  <EmptyState title="No line items" />
                ) : (
                  <>
                    {items.map((it) => (
                      <div key={it.id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted">
                        <div>
                          <p className="text-[12.5px] font-semibold text-ink">{productNames[it.sku] ?? it.sku}</p>
                          <p className="text-[11px] text-ink-faint">{it.sku} · Qty {it.qty} · ৳{(it.unit_price_bdt ?? 0).toLocaleString()} each</p>
                        </div>
                        <p className="text-[13px] font-bold text-ink">৳{(it.line_total_bdt ?? 0).toLocaleString()}</p>
                      </div>
                    ))}
                    <div className="flex items-center justify-between px-2 pt-3 mt-2 border-t border-border-subtle">
                      <p className="text-[12.5px] font-semibold text-ink-muted">Total</p>
                      <p className="text-[15px] font-bold text-ink">৳{order.total_bdt.toLocaleString()}</p>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>

            {order.status !== 'cancelled' && (
              <Card>
                <CardHeader
                  title="Scan Units"
                  subtitle="Link each physical unit's barcode to this order for exact serial traceability"
                  icon={<ScanLine className="w-4 h-4" />}
                />
                <CardBody className="space-y-3">
                  <form onSubmit={handleScanUnit} className="flex items-end gap-2.5">
                    <div className="flex-1">
                      <Field label="Scan or type barcode">
                        <Input
                          autoFocus
                          value={scanUnitInput}
                          onChange={(e) => setScanUnitInput(e.target.value.toUpperCase())}
                          placeholder="Scan barcode…"
                          className="font-mono"
                        />
                      </Field>
                    </div>
                    <Button type="submit" loading={scanningUnit} disabled={!scanUnitInput.trim()}>Link Unit</Button>
                  </form>
                  <div className="space-y-1">
                    {items.map((it) => {
                      const components = bundleComponents[it.sku];
                      if (components) {
                        return (
                          <div key={it.id} className="py-1">
                            <p className="text-[11px] font-semibold text-ink-faint px-2 mb-0.5">{productNames[it.sku] ?? it.sku} ({it.qty} set{it.qty > 1 ? 's' : ''})</p>
                            {components.map((c) => {
                              const needed = c.qty_per_bundle * it.qty;
                              const scanned = scannedCounts[c.component_sku] ?? 0;
                              const complete = scanned >= needed;
                              return (
                                <div key={c.component_sku} className="flex items-center justify-between px-2 py-1">
                                  <span className="text-[12px] text-ink-faint pl-2">{productNames[c.component_sku] ?? c.component_sku}</span>
                                  <Badge tone={complete ? 'success' : 'warning'}>{scanned}/{needed} scanned</Badge>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                      const scanned = scannedCounts[it.sku] ?? 0;
                      const complete = scanned >= it.qty;
                      return (
                        <div key={it.id} className="flex items-center justify-between px-2 py-1.5">
                          <span className="text-[12px] text-ink-faint">{productNames[it.sku] ?? it.sku}</span>
                          <Badge tone={complete ? 'success' : 'warning'}>{scanned}/{it.qty} scanned</Badge>
                        </div>
                      );
                    })}
                  </div>
                </CardBody>
              </Card>
            )}

            <Card>
              <CardHeader title="Timeline (타임라인)" icon={<CircleDot className="w-4 h-4" />} />
              <CardBody className="space-y-3">
                {timeline.map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    {t.done ? <CheckCircle2 className="w-4 h-4 text-success flex-shrink-0" /> : <CircleDot className="w-4 h-4 text-ink-disabled flex-shrink-0" />}
                    <p className={`text-[12.5px] flex-1 ${t.done ? 'text-ink font-semibold' : 'text-ink-disabled'}`}>{t.label}</p>
                    <p className="text-[11px] text-ink-faint">{t.at ? new Date(t.at).toLocaleString() : '—'}</p>
                  </div>
                ))}
              </CardBody>
            </Card>

            {returns.length > 0 && (
              <Card>
                <CardHeader title="Returns (반품)" icon={<Undo2 className="w-4 h-4" />} subtitle={`${returns.length} request(s)`} />
                <CardBody className="space-y-1.5">
                  {returns.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-2 py-2 rounded-lg hover:bg-surface-muted">
                      <div>
                        <p className="text-[12.5px] font-semibold text-ink capitalize">{r.type}</p>
                        {r.reason && <p className="text-[11px] text-ink-faint">{r.reason}</p>}
                      </div>
                      <Badge tone={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'error' : 'warning'}>{r.status}</Badge>
                    </div>
                  ))}
                </CardBody>
              </Card>
            )}
          </div>

          <div className="space-y-5">
            <Card>
              <CardHeader
                title="Customer (고객)"
                icon={<User className="w-4 h-4" />}
                action={
                  order.customer_id && (
                    <Link to={`/admin/customers/${order.customer_id}`} className="text-[11px] font-semibold text-primary-800 hover:underline inline-flex items-center gap-1">
                      Account <ExternalLink className="w-3 h-3" />
                    </Link>
                  )
                }
              />
              <CardBody className="space-y-1.5 text-[12.5px]">
                <p className="text-ink font-semibold">{order.customer_info?.name || '—'}</p>
                <div className="flex items-center gap-2">
                  <p className="text-ink-muted">{order.customer_info?.phone || '—'}</p>
                  {order.customer_info?.phone && (
                    <button
                      type="button"
                      onClick={() => callNumber(order.customer_info!.phone!)}
                      className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-50 text-primary-800 hover:bg-primary-100 cursor-pointer flex-shrink-0"
                      title={`Call ${order.customer_info.phone}`}
                    >
                      <Phone className="w-3 h-3" />
                    </button>
                  )}
                  {fraudRating && (
                    <Link to="/admin/fraud-check" title="From courier delivery history — open Fraud Check for details">
                      <Badge tone={fraudRating.tone}>
                        {fraudRating.ratio}% — {fraudRating.label}
                      </Badge>
                    </Link>
                  )}
                </div>
                <p className="text-ink-faint">{order.customer_info?.address || 'No address on file'}</p>
                {!order.customer_id && <Badge tone="neutral">Guest checkout</Badge>}

                {orderCalls.length > 0 && (
                  <div className="pt-2 mt-1 border-t border-border-subtle space-y-1.5">
                    <p className="text-[10.5px] font-bold tracking-wider uppercase text-ink-disabled">Confirmation Calls</p>
                    {orderCalls.map((c) => (
                      <div key={c.id} className="flex items-center gap-2">
                        {c.direction === 'outbound' ? (
                          <PhoneOutgoing className="w-3 h-3 text-ink-faint flex-shrink-0" />
                        ) : (
                          <PhoneIncoming className="w-3 h-3 text-ink-faint flex-shrink-0" />
                        )}
                        <span className="text-ink-muted flex-1 min-w-0 truncate">
                          {c.user_name || 'Staff'} · {new Date(c.started_at).toLocaleString()}
                          {c.duration_seconds ? ` · ${Math.floor(c.duration_seconds / 60)}:${String(c.duration_seconds % 60).padStart(2, '0')}` : ''}
                        </span>
                        <Badge tone={CALL_STATUS_TONE[c.status] ?? 'neutral'}>{CALL_STATUS_LABEL[c.status] ?? c.status}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Payment (결제)" icon={<ShoppingCart className="w-4 h-4" />} />
              <CardBody className="space-y-1.5 text-[12.5px]">
                <div className="flex items-center justify-between">
                  <span className="text-ink-faint">Method</span>
                  <span className="text-ink font-semibold">{formatPaymentMethodLabel(order.payment_method, gatewayNames)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ink-faint">Total</span>
                  <span className="text-ink font-bold">৳{order.total_bdt.toLocaleString()}</span>
                </div>
                {latestShipment && (
                  <div className="flex items-center justify-between pt-1.5 mt-1.5 border-t border-border-subtle">
                    <span className="text-ink-faint">COD Status</span>
                    <div className="flex items-center gap-2">
                      <Badge tone={latestShipment.cod_status === 'collected' || latestShipment.cod_status === 'remitted' ? 'success' : latestShipment.cod_status === 'failed' ? 'error' : 'warning'}>
                        {latestShipment.cod_status}
                      </Badge>
                      {order.payment_method === 'cod' && order.status === 'delivered' && latestShipment.cod_status === 'pending' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => {
                            setCodForm({ amount_bdt: String(order.total_bdt), note: '' });
                            setCodModal(true);
                          }}
                        >
                          Mark Collected
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {latestShipment && (
              <Card>
                <CardHeader title="Shipment (배송)" icon={<Truck className="w-4 h-4" />} />
                <CardBody className="space-y-1.5 text-[12.5px]">
                  <div className="flex items-center justify-between">
                    <span className="text-ink-faint">Courier</span>
                    <span className="text-ink font-semibold">{liveCourierName ?? latestShipment.courier ?? '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ink-faint">Tracking No.</span>
                    <span className="text-ink">{latestShipment.tracking_no || '—'}</span>
                  </div>
                  {latestShipment.courier_status && (
                    <div className="flex items-center justify-between">
                      <span className="text-ink-faint">Courier Status</span>
                      <Badge tone="info">{latestShipment.courier_status}</Badge>
                    </div>
                  )}
                  {latestShipment.status_checked_at && (
                    <p className="text-[11px] text-ink-faint">Last checked {new Date(latestShipment.status_checked_at).toLocaleString()}</p>
                  )}
                  <Button
                    size="sm"
                    variant="secondary"
                    className="w-full mt-1"
                    onClick={() => handleCheckStatus(latestShipment.id)}
                    loading={checkingStatusId === latestShipment.id}
                    disabled={!latestShipment.tracking_no}
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Check Status
                  </Button>
                </CardBody>
              </Card>
            )}

            <InvoiceCard orderId={order.id} orderStatus={order.status} paymentMethod={order.payment_method} />
          </div>
        </div>
      </div>

      <Modal open={confirmModalOpen} onClose={() => setConfirmModalOpen(false)} title="Confirm Order" subtitle={order.order_no} icon={<CheckCircle2 className="w-4 h-4" />} busy={busy} maxWidth="max-w-md">
        <div className="space-y-3.5">
          <div className="rounded-xl border border-border-subtle p-3.5 space-y-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-ink-faint mb-1">Ship To</p>
            <p className="text-[13.5px] font-semibold text-ink">{order.customer_info?.name || '—'}</p>
            <p className="text-[12.5px] text-ink-faint">{order.customer_info?.phone || '—'}</p>
            <p className="text-[12.5px] text-ink-faint">{order.customer_info?.address || '—'}</p>
          </div>

          <Field label="Courier">
            {couriers.length === 0 ? (
              <p className="text-[12.5px] text-error">No courier is enabled — enable one under Couriers settings first.</p>
            ) : (
              <Select value={selectedCourierKey} onChange={(e) => setSelectedCourierKey(e.target.value)}>
                {couriers.map((c) => (
                  <option key={c.courier_key} value={c.courier_key}>
                    {c.display_name}
                    {c.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Button className="w-full" loading={busy} disabled={!selectedCourierKey} onClick={() => handleConfirm(selectedCourierKey)}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Confirm &amp; Book Shipment
          </Button>
        </div>
      </Modal>

      <Modal open={codModal} onClose={() => setCodModal(false)} title="Mark COD Collected (COD 수금 확인)" subtitle={order.order_no} icon={<CreditCard className="w-4 h-4" />} busy={busy}>
        <form onSubmit={handleRecordCod} className="space-y-3">
          <Field label="Amount Collected (BDT)">
            <Input type="number" min={0} required value={codForm.amount_bdt} onChange={(e) => setCodForm((f) => ({ ...f, amount_bdt: e.target.value }))} />
          </Field>
          <Field label="Note (optional)">
            <Input value={codForm.note} onChange={(e) => setCodForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. Collected by courier on delivery" />
          </Field>
          <Button type="submit" loading={busy} className="w-full">Confirm Collected</Button>
        </form>
      </Modal>

      <ShipModal open={shipModal} onClose={() => setShipModal(false)} onSubmit={handleShip} orderNo={order.order_no} busy={busy} />

      <Modal open={cancelModal} onClose={() => setCancelModal(false)} title="Cancel Order" subtitle={order.order_no} icon={<Ban className="w-4 h-4" />} busy={busy}>
        <form onSubmit={handleCancelOrder} className="space-y-3">
          <Field label="Reason for cancellation">
            <Textarea required rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g. Customer requested cancellation, out of stock, duplicate order…" />
          </Field>
          <Button type="submit" variant="danger" loading={busy} disabled={!cancelReason.trim()} className="w-full">Confirm Cancel</Button>
        </form>
      </Modal>

      <Modal open={returnModal} onClose={() => setReturnModal(false)} title="Request Return (반품 요청)" subtitle={order.order_no} icon={<Undo2 className="w-4 h-4" />} busy={busy}>
        <form onSubmit={handleRequestReturn} className="space-y-3">
          <Field label="Type">
            <Select value={returnForm.type} onChange={(e) => setReturnForm((f) => ({ ...f, type: e.target.value }))}>
              <option value="refund">Refund</option>
              <option value="exchange">Exchange</option>
            </Select>
          </Field>
          <Field label="Reason">
            <Input required value={returnForm.reason} onChange={(e) => setReturnForm((f) => ({ ...f, reason: e.target.value }))} />
          </Field>
          <Field label={`Qty to restock (order has ${items.reduce((sum, it) => sum + it.qty, 0)} units total)`}>
            <Input
              type="number"
              min={1}
              max={items.reduce((sum, it) => sum + it.qty, 0)}
              required
              value={returnForm.qty}
              onChange={(e) => setReturnForm((f) => ({ ...f, qty: e.target.value }))}
            />
          </Field>
          <Button type="submit" loading={busy} className="w-full">Submit Request</Button>
        </form>
      </Modal>

      <PathaoLocationModal
        open={pathaoModalOpen}
        onClose={() => setPathaoModalOpen(false)}
        onSubmit={handlePathaoLocationSubmit}
        busy={pathaoBusy}
        customerName={order.customer_info?.name}
        customerPhone={order.customer_info?.phone}
        customerAddress={order.customer_info?.address}
      />
    </AppShell>
  );
}
